import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://sy47l7fjif.execute-api.us-east-1.amazonaws.com/Prod";
const PAGE_SIZE = 50;

const Badge = ({ children, kind = "default" }) => {
  const base =
    "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap";
  const styles = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-100 text-green-700 border-green-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    red: "bg-red-100 text-red-700 border-red-200",
    orange: "bg-orange-100 text-orange-700 border-orange-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200",
  };

  return (
    <span className={`${base} ${styles[kind] || styles.default}`}>
      {children || "-"}
    </span>
  );
};

const StatCard = ({ title, value, hint, kind = "default" }) => {
  const accents = {
    default: "from-gray-50 to-white border-gray-200",
    blue: "from-blue-50 to-white border-blue-200",
    green: "from-green-50 to-white border-green-200",
    yellow: "from-yellow-50 to-white border-yellow-200",
    red: "from-red-50 to-white border-red-200",
  };

  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${
        accents[kind] || accents.default
      } p-6 shadow-sm`}
    >
      <div className="text-sm text-gray-500">{title}</div>
      <div className="mt-3 text-3xl font-bold text-gray-900">{value}</div>
      {hint ? <div className="mt-2 text-sm text-gray-500">{hint}</div> : null}
    </div>
  );
};

const EmptyState = ({ title, text }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
    <div className="text-lg font-semibold text-gray-800">{title}</div>
    <p className="mt-2 text-sm text-gray-500">{text}</p>
  </div>
);

const formatCurrency = (amount, currency = "EUR") => {
  const numeric = Number(amount || 0);
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(2)} ${currency || "EUR"}`;
  }
};

const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  const raw = String(dateValue).trim();

  const deMatch = raw.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/);
  if (deMatch) {
    const [, d, m, y] = deMatch;
    const fullYear = y.length === 2 ? `20${y}` : y;
    return `${String(d).padStart(2, "0")}.${String(m).padStart(
      2,
      "0"
    )}.${fullYear}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("de-DE");
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return dateValue;
  return d.toLocaleString("de-DE");
};

const getCompanyId = () => {
  const direct = localStorage.getItem("company_id");
  if (direct) return direct;

  try {
    const ctxRaw = localStorage.getItem("company_context");
    if (!ctxRaw) return "";
    const ctx = JSON.parse(ctxRaw);
    return ctx.company_id || "";
  } catch {
    return "";
  }
};

const buildQuery = (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;

  let text = String(value).trim();
  if (!text) return 0;

  text = text.replace(/€/g, "").replace(/EUR/gi, "").replace(/\s/g, "");

  if (text.includes(",") && text.includes(".")) {
    text = text.replace(/\./g, "").replace(",", ".");
  } else if (text.includes(",")) {
    text = text.replace(",", ".");
  }

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeBool = (value) => {
  if (typeof value === "boolean") return value;
  if (value === null || value === undefined) return false;

  const text = String(value).trim().toLowerCase();
  return [
    "true",
    "1",
    "yes",
    "ja",
    "y",
    "geschlossen",
    "abgelegt",
    "closed",
    "erledigt",
    "abgeschlossen",
  ].includes(text);
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getSortableDateValue = (value) => {
  if (!value) return 0;

  const raw = String(value).trim();

  const deMatch = raw.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/);
  if (deMatch) {
    const [, d, m, y] = deMatch;
    const fullYear = y.length === 2 ? `20${y}` : y;
    const iso = `${fullYear}-${String(m).padStart(2, "0")}-${String(d).padStart(
      2,
      "0"
    )}`;
    const ts = new Date(iso).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  }

  const ts = new Date(raw).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

const sortCases = (items, sortBy) => {
  const sorted = [...items];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case "az_asc":
        return normalizeText(a.az).localeCompare(normalizeText(b.az), "de");
      case "az_desc":
        return normalizeText(b.az).localeCompare(normalizeText(a.az), "de");

      case "schuldner_asc":
        return normalizeText(a.schuldner).localeCompare(
          normalizeText(b.schuldner),
          "de"
        );
      case "schuldner_desc":
        return normalizeText(b.schuldner).localeCompare(
          normalizeText(a.schuldner),
          "de"
        );

      case "offene_hf_desc":
        return Number(b.offene_hf || 0) - Number(a.offene_hf || 0);
      case "offene_hf_asc":
        return Number(a.offene_hf || 0) - Number(b.offene_hf || 0);

      case "last_event_date_asc":
        return (
          getSortableDateValue(a.last_event_date) -
          getSortableDateValue(b.last_event_date)
        );

      case "last_event_date_desc":
      default:
        return (
          getSortableDateValue(b.last_event_date) -
          getSortableDateValue(a.last_event_date)
        );
    }
  });

  return sorted;
};

const getStadiumKind = (value) => {
  const text = String(value || "").trim().toLowerCase();

  if (text.includes("vorgericht")) return "yellow";
  if (text.includes("mahnbescheid")) return "orange";
  if (text.includes("vollstreck")) return "red";
  if (text.includes("insol")) return "purple";
  if (text.includes("raten")) return "blue";
  if (text.includes("abgeschlossen") || text.includes("erledigt")) return "green";

  return "default";
};

const getStatusKind = (value) => {
  const text = String(value || "").trim().toLowerCase();

  if (text.includes("offen") || text.includes("neu") || text.includes("aktiv")) {
    return "blue";
  }

  if (
    text.includes("erledigt") ||
    text.includes("abgeschlossen") ||
    text.includes("geschlossen")
  ) {
    return "green";
  }

  if (text.includes("pause") || text.includes("wart")) {
    return "yellow";
  }

  return "default";
};

const normalizeCase = (item = {}, index = 0) => ({
  case_id:
    item.case_id ||
    item.id ||
    item.az ||
    item.aktenzeichen ||
    item.mandant_az ||
    `case-${index}`,

  az: item.az || item.aktenzeichen || item.case_id || "",

  mandant_az:
    item.mandant_az ||
    item.mandanten_az ||
    item.mandanten_aktenzeichen ||
    item.mandatsreferenz ||
    item.mandatsnummer ||
    "",

  schuldner:
    item.schuldner ||
    item.schuldner_name ||
    item.schuldner_full_name ||
    item.debtor_name ||
    item.debtor ||
    item.debtor_full_name ||
    item.person_name ||
    item.firma ||
    item.firmenname ||
    "",

  ort:
    item.schuldner_ort ||
    item.ort ||
    item.stadt ||
    item.wohnort ||
    item.city ||
    "",

  stadium:
    item.stadium ||
    item.verfahrensstand ||
    item.status_stadium ||
    item.aktenstatus ||
    item.stage ||
    "",

  bearb_status:
    item.bearb_status ||
    item.bearbeitungsstatus ||
    item.status ||
    "",

  offene_hf: normalizeNumber(
    item.offene_hf ??
      item.offene_hauptforderung ??
      item.hauptforderung_offen ??
      item.saldo_offen ??
      item.saldo ??
      item.offen ??
      item.betrag ??
      item.hauptforderung ??
      item.principal_amount ??
      0
  ),

  uebern_hf: normalizeNumber(
    item.uebern_hf ??
      item.uebernommene_hf ??
      item.uebernommene_hauptforderung ??
      0
  ),

  uebern_kosten: normalizeNumber(
    item.uebern_kosten ??
      item.uebernommene_kosten ??
      item.kosten ??
      item.auslagen ??
      0
  ),

  vadatum: item.vadatum || item.va_datum || "",
  evdatum: item.evdatum || item.ev_datum || "",

  last_event_text:
    item.last_event_text ||
    item.kurztext_sachstand ||
    item.kurzttext_sachstand ||
    item.sachstand ||
    item.letztes_ereignis ||
    item.ereignistext ||
    item.letzter_stand ||
    item.status_text ||
    "",

  last_event_date:
    item.last_event_date ||
    item.uebernahmedatum ||
    item.letztes_ereignis_datum ||
    item.ereignisdatum ||
    item.datum ||
    item.updated_at ||
    item.last_update ||
    null,

  abschlussdatum: item.abschlussdatum || "",

  is_closed:
    normalizeBool(
      item.is_closed ??
        item.geschlossen ??
        item.abgelegt ??
        item.closed ??
        item.abschlussdatum
    ) ||
    ["geschlossen", "abgeschlossen", "erledigt"].includes(
      String(item.bearb_status || item.status || "").toLowerCase()
    ),
});

const normalizeStats = (raw = {}, normalizedCases = []) => ({
  offeneAkten:
    raw.offeneAkten ??
    raw.open_cases ??
    raw.total_cases ??
    raw.count ??
    normalizedCases.filter((item) => !item.is_closed).length ??
    0,

  offeneHf:
    raw.offeneHf ??
    raw.offene_hf ??
    raw.total_hf ??
    raw.open_principal ??
    raw.total_open_amount ??
    normalizedCases.reduce((sum, item) => sum + Number(item.offene_hf || 0), 0),

  uebernommeneHf:
    raw.uebernommeneHf ??
    raw.uebern_hf ??
    raw.total_uebern_hf ??
    normalizedCases.reduce((sum, item) => sum + Number(item.uebern_hf || 0), 0),

  uebernommeneKosten:
    raw.uebernommeneKosten ??
    raw.uebern_kosten ??
    raw.total_uebern_kosten ??
    normalizedCases.reduce((sum, item) => sum + Number(item.uebern_kosten || 0), 0),

  letzteAktualisierung:
    raw.letzteAktualisierung ??
    raw.last_updated_at ??
    raw.last_import_at ??
    raw.updated_at ??
    null,
});

const MandantCases = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [stadium, setStadium] = useState("");
  const [bearbeitungsstatus, setBearbeitungsstatus] = useState("");
  const [nurOffene, setNurOffene] = useState(true);

  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({
    offeneAkten: 0,
    offeneHf: 0,
    uebernommeneHf: 0,
    uebernommeneKosten: 0,
    letzteAktualisierung: null,
  });

  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("last_event_date_desc");

  const toggleRow = (caseId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [caseId]: !prev[caseId],
    }));
  };

  const loadCases = async () => {
    try {
      setLoading(true);
      setError("");

      const companyId = getCompanyId();

      if (!companyId) {
        throw new Error("Keine company_id gefunden. Bitte erneut einloggen.");
      }

      const casesQuery = buildQuery({
        company_id: companyId,
        search: search || undefined,
        stadium: stadium || undefined,
        bearb_status: bearbeitungsstatus || undefined,
      });

      const casesUrl = `${API_BASE}/mandant/cases?${casesQuery}`;

      const casesRes = await fetch(casesUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-company-id": companyId,
        },
      });

      if (!casesRes.ok) {
        const errorText = await casesRes.text();
        console.error("MANDANT CASES ERROR", errorText);
        throw new Error(
          `Akten konnten nicht geladen werden (${casesRes.status}).`
        );
      }

      const casesData = await casesRes.json();

      const rawItems = Array.isArray(casesData)
        ? casesData
        : Array.isArray(casesData.items)
        ? casesData.items
        : Array.isArray(casesData.cases)
        ? casesData.cases
        : [];

      const normalizedCases = rawItems.map((item, index) =>
        normalizeCase(item, index)
      );

      const visibleCases = nurOffene
        ? normalizedCases.filter((item) => !item.is_closed)
        : normalizedCases;

      setCases(visibleCases);
      setExpandedRows({});
      setPage(1);

      if (casesData?.summary) {
        setStats(normalizeStats(casesData.summary, visibleCases));
      } else {
        try {
          const statsQuery = buildQuery({
            company_id: companyId,
          });

          const statsUrl = `${API_BASE}/mandant/cases/stats?${statsQuery}`;

          const statsRes = await fetch(statsUrl, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-company-id": companyId,
            },
          });

          if (statsRes.ok) {
            const statsData = await statsRes.json();
            setStats(normalizeStats(statsData, visibleCases));
          } else {
            setStats(normalizeStats({}, visibleCases));
          }
        } catch (statsErr) {
          console.warn("Stats fallback fehlgeschlagen", statsErr);
          setStats(normalizeStats({}, visibleCases));
        }
      }
    } catch (err) {
      console.error("MandantCases load error", err);
      setCases([]);
      setStats({
        offeneAkten: 0,
        offeneHf: 0,
        uebernommeneHf: 0,
        uebernommeneKosten: 0,
        letzteAktualisierung: null,
      });
      setError(err.message || "Akten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, stadium, bearbeitungsstatus, nurOffene, sortBy]);

  const filteredCases = useMemo(() => {
    const filtered = cases.filter((item) => {
      const haystack = [
        item.az,
        item.mandant_az,
        item.schuldner,
        item.ort,
        item.stadium,
        item.bearb_status,
        item.last_event_text,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !search || haystack.includes(search.toLowerCase());
      const matchesStadium = !stadium || item.stadium === stadium;
      const matchesStatus =
        !bearbeitungsstatus || item.bearb_status === bearbeitungsstatus;
      const matchesOpen = !nurOffene || !item.is_closed;

      return matchesSearch && matchesStadium && matchesStatus && matchesOpen;
    });

    return sortCases(filtered, sortBy);
  }, [cases, search, stadium, bearbeitungsstatus, nurOffene, sortBy]);

  const stadiumOptions = [
    ...new Set(cases.map((item) => item.stadium).filter(Boolean)),
  ];

  const statusOptions = [
    ...new Set(cases.map((item) => item.bearb_status).filter(Boolean)),
  ];

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const paginatedCases = filteredCases.slice(pageStart, pageEnd);

  const handleExportFilteredCSV = () => {
    if (!filteredCases || filteredCases.length === 0) {
      alert("Keine gefilterten Akten zum Export vorhanden.");
      return;
    }

    const headers = [
      "AZ",
      "Mandanten-AZ",
      "Schuldner",
      "Ort",
      "Stadium",
      "Bearbeitungsstatus",
      "Übernommene HF",
      "Offene HF",
      "Auslagen",
      "Letzter Stand",
      "Letztes Update",
      "VA-Datum",
      "EV-Datum",
      "Abschlussdatum",
      "Offen",
    ];

    const rows = filteredCases.map((item) => [
      item.az || "",
      item.mandant_az || "",
      item.schuldner || "",
      item.ort || "",
      item.stadium || "",
      item.bearb_status || "",
      item.uebern_hf ?? 0,
      item.offene_hf ?? 0,
      item.uebern_kosten ?? 0,
      item.last_event_text || "",
      item.last_event_date || "",
      item.vadatum || "",
      item.evdatum || "",
      item.abschlussdatum || "",
      item.is_closed ? "Nein" : "Ja",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`).join(";")
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "akten_gefiltert_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-[1320px] mx-auto px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-3xl font-bold text-gray-900">
              Aktenübersicht
            </div>
            <div className="mt-1 text-sm text-gray-500">
              Aktueller Stand Ihrer Inkassoakten
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Badge kind="yellow">Mandantensicht</Badge>
            <button
              type="button"
              onClick={() => navigate("/company-dashboard")}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Zurück zum Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1320px] mx-auto px-8 py-10">
        {loading && (
          <div className="rounded-2xl bg-white border shadow-sm p-6 mb-8">
            <div className="text-gray-700 font-medium">Lade Akten ...</div>
            <div className="mt-2 text-sm text-gray-500">
              Die aktuelle Aktenliste wird geladen.
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl bg-red-50 border border-red-200 shadow-sm p-5 mb-8">
            <div className="text-red-800 font-semibold">Fehler</div>
            <div className="mt-1 text-sm text-red-700">{error}</div>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
              <StatCard
                title="Offene Akten"
                value={stats.offeneAkten}
                hint="Aktueller offener Bestand"
                kind="yellow"
              />
              <StatCard
                title="Offene HF"
                value={formatCurrency(stats.offeneHf, "EUR")}
                hint="Summierter offener Betrag"
                kind="green"
              />
              <StatCard
                title="Übernommene HF"
                value={formatCurrency(stats.uebernommeneHf, "EUR")}
                hint="Übernommene Hauptforderungen"
                kind="blue"
              />
              <StatCard
                title="Auslagen"
                value={formatCurrency(stats.uebernommeneKosten, "EUR")}
                hint="Gläubigerseitige Auslagen"
                kind="default"
              />
            </div>

            <div className="grid grid-cols-1 mb-8">
              <StatCard
                title="Letzte Aktualisierung"
                value={formatDateTime(stats.letzteAktualisierung)}
                hint="Zeitpunkt des letzten Imports"
                kind="blue"
              />
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sortierung
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white"
                  >
                    <option value="last_event_date_desc">
                      Letztes Update absteigend
                    </option>
                    <option value="last_event_date_asc">
                      Letztes Update aufsteigend
                    </option>
                    <option value="az_asc">AZ aufsteigend</option>
                    <option value="az_desc">AZ absteigend</option>
                    <option value="schuldner_asc">Schuldner A–Z</option>
                    <option value="schuldner_desc">Schuldner Z–A</option>
                    <option value="offene_hf_desc">Offene HF absteigend</option>
                    <option value="offene_hf_asc">Offene HF aufsteigend</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Suche
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="AZ, Mandanten-AZ, Schuldner, Ort ..."
                      className="w-full border border-gray-300 rounded-xl px-4 py-3"
                    />
                    <button
                      type="button"
                      onClick={loadCases}
                      className="px-5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
                    >
                      Laden
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stadium
                  </label>
                  <select
                    value={stadium}
                    onChange={(e) => setStadium(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white"
                  >
                    <option value="">Alle</option>
                    {stadiumOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bearbeitungsstatus
                  </label>
                  <select
                    value={bearbeitungsstatus}
                    onChange={(e) => setBearbeitungsstatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white"
                  >
                    <option value="">Alle</option>
                    {statusOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={nurOffene}
                      onChange={(e) => setNurOffene(e.target.checked)}
                    />
                    <span>Nur offene Akten anzeigen</span>
                  </label>
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={handleExportFilteredCSV}
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Gefilterte Liste herunterladen
                </button>
              </div>
            </div>

            {filteredCases.length === 0 ? (
              <EmptyState
                title="Keine Akten gefunden"
                text="Entweder liegt noch kein Import vor oder die aktuelle Filterkombination liefert keine Treffer."
              />
            ) : (
              <>
                <div className="flex items-center justify-between mb-4 px-1">
                  <div className="text-sm text-gray-500">
                    Zeige {pageStart + 1} bis{" "}
                    {Math.min(pageEnd, filteredCases.length)} von{" "}
                    {filteredCases.length} Akten
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={safePage <= 1}
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm disabled:opacity-50"
                    >
                      Zurück
                    </button>
                    <div className="text-sm text-gray-600">
                      Seite {safePage} / {totalPages}
                    </div>
                    <button
                      type="button"
                      disabled={safePage >= totalPages}
                      onClick={() =>
                        setPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm disabled:opacity-50"
                    >
                      Weiter
                    </button>
                  </div>
                </div>

                <div className="max-w-[1200px] rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-[1100px] w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-gray-600">
                          <th className="px-5 py-4 font-semibold">AZ</th>
                          <th className="px-5 py-4 font-semibold">Mandanten-AZ</th>
                          <th className="px-5 py-4 font-semibold">Schuldner</th>
                          <th className="px-5 py-4 font-semibold">Ort</th>
                          <th className="px-5 py-4 font-semibold">Stadium</th>
                          <th className="px-5 py-4 font-semibold text-right">
                            Übernommene HF
                          </th>
                          <th className="px-5 py-4 font-semibold text-right">
                            Offene HF
                          </th>
                          <th className="px-5 py-4 font-semibold text-right"></th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 bg-white">
                        {paginatedCases.map((item, index) => {
                          const rowKey =
                            item.case_id || `${item.az || "case"}-${index}`;
                          const isExpanded = !!expandedRows[rowKey];

                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => toggleRow(rowKey)}
                              >
                                <td className="px-5 py-4 align-top font-medium text-gray-900">
                                  {item.az || "-"}
                                </td>
                                <td className="px-5 py-4 align-top text-gray-700">
                                  {item.mandant_az || "-"}
                                </td>
                                <td className="px-5 py-4 align-top text-gray-900">
                                  {item.schuldner || "-"}
                                </td>
                                <td className="px-5 py-4 align-top text-gray-700">
                                  {item.ort || "-"}
                                </td>
                                <td className="px-5 py-4 align-top">
                                  <Badge kind={getStadiumKind(item.stadium)}>
                                    {item.stadium || "-"}
                                  </Badge>
                                </td>
                                <td className="px-5 py-4 align-top text-right font-medium text-gray-900">
                                  {formatCurrency(item.uebern_hf || 0, "EUR")}
                                </td>
                                <td className="px-5 py-4 align-top text-right font-medium text-gray-900">
                                  {formatCurrency(item.offene_hf || 0, "EUR")}
                                </td>
                                <td className="px-5 py-4 align-top text-right text-gray-500">
                                  {isExpanded ? "–" : "+"}
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr className="bg-gray-50">
                                  <td colSpan={8} className="px-6 py-6">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                        <div className="mb-3 text-sm font-semibold text-gray-900">
                                          Beträge
                                        </div>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Übernommene HF
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatCurrency(item.uebern_hf || 0)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Offene HF
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatCurrency(item.offene_hf || 0)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Auslagen
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatCurrency(item.uebern_kosten || 0)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                        <div className="mb-3 text-sm font-semibold text-gray-900">
                                          Verfahren
                                        </div>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Letzter Stand
                                            </span>
                                            <span className="font-medium text-gray-900 text-right">
                                              {item.last_event_text || "-"}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Letztes Update
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatDate(item.last_event_date)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              VA-Datum
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatDate(item.vadatum)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              EV-Datum
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {formatDate(item.evdatum)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                        <div className="mb-3 text-sm font-semibold text-gray-900">
                                          Status
                                        </div>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">Stadium</span>
                                            <span>
                                              <Badge kind={getStadiumKind(item.stadium)}>
                                                {item.stadium || "-"}
                                              </Badge>
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Bearbeitungsstatus
                                            </span>
                                            <span>
                                              <Badge kind={getStatusKind(item.bearb_status)}>
                                                {item.bearb_status || "-"}
                                              </Badge>
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-gray-500">
                                              Offen
                                            </span>
                                            <span className="font-medium text-gray-900">
                                              {item.is_closed ? "Nein" : "Ja"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MandantCases;